import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import Box from '@components/base/Box'
import { pushFunnelLoginEvent } from '@components/base/GoogleTags'
import { GET_EVENT_BY_ID } from '@graphql/events/GetEventById'
import { GET_EVENT_BY_ID_FOR_UNREGISTERED_USER } from '@graphql/events/GetEventByIdForUnregisteredUser'
import { GET_EVENT_BY_DONATION_URL } from '@graphql/events/GetEventByDonationUrl'
import { PURCHASE_TICKETS } from '@graphql/events/purchaseTickets'
import { CHECK_DISCOUNT_CODE } from '@graphql/events/checkDiscountCode'
import {
  DiscountType,
  DonationInput,
  Event,
  EventAttendee,
  EventDetails,
  EventFundraising,
  EventType,
  FundraisingProgram,
  TicketPurchaseInput,
  TicketType,
} from '@graphql/__generated/graphql'
import { GET_USER_STATUS } from '@graphql/users/getUserStatus'
import { RedirectTarget } from '@components/authforms/CodeAuthForm'
import { MiniNavInput } from '@components/base/MiniNav'
import currencies from '@libs/currencies'
import { getAvailableTickets } from '@libs/Event/util'
import { useAuth } from '@libs/Auth/useAuth'
import useLocalStorage from '@libs/useLocalStorage'
import { useOrganisationProfile } from '@libs/useOrganisationProfile'
import useStatistics from '@libs/useStatistics'
import { useTheme } from '@libs/useTheme'
import {
  areAllTicketRegistrationFieldsFilled,
  CONFIG_OPTIONS,
  getAdditionalTicketsForCount,
  getRegistrationConfig,
  getRegistrationFieldErrors,
  getRegistrationVideoUrl,
  isVatIncludedForOrganisation,
  resolveRegistrationEventId,
} from '@libs/Utility/register.util'
import { debounce, getDynamicStyle, openInSameTab } from '@libs/Utility/util'
import { PASSWORD_PATTERN } from '@libs/Utility/validation'
import { validate } from '@components/inputs/Validations'
import { loadStripe } from '@stripe/stripe-js'
import { isArray } from 'lodash'
import { useRouter } from 'next/router'
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { eventIdsMap } from 'pages/event/[eventId]'

const stripePublicKey = process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY as string

const normalizeEmail = (value?: string | null) => value?.trim().toLowerCase()

export type RegistrationDonation = Partial<DonationInput> & {
  enabled: boolean
}

type RegistrationAccountData = {
  confirmPassword?: string
  email?: string
  firstName?: string
  lastName?: string
  password?: string
}

type RegistrationStepOptions = {
  accountData?: RegistrationAccountData
  goToTab?: (tab: string) => void
  onSignedIn?: () => void
  validatePasswordConfirmation?: boolean
}

type UseEventRegistrationOptions = {
  embedCheckoutReturn?: boolean
}

type CognitoSessionLike = {
  getIdToken?: () => {
    payload?: Record<string, string | undefined>
  }
}

export const useEventRegistration = (
  options: UseEventRegistrationOptions = {}
) => {
  const router = useRouter()
  const statisticId = useStatistics()
  const userData = isArray(router.query.ud)
    ? (router.query.ud[0] ?? '').trim().replace(' ', '+')
    : (router?.query?.ud ?? '').trim().replace(' ', '+')

  const discountParam = isArray(router.query.dcu)
    ? router.query.dcu[0]
    : router.query.dcu
  const discountedUrl = discountParam
    ? Buffer.from(discountParam.trim().replace(/ /g, '+'), 'base64').toString()
    : undefined

  const {
    getSession,
    signInWithEmail,
    signInWithGoogle,
    signInWithLinkedIn,
    profile,
    getPermission,
  } = useAuth()
  const [purchaseTickets] = useMutation(PURCHASE_TICKETS)

  const [email, setEmail] = useState<string>()
  const [password, setPassword] = useState<string>()
  const [confirmPassword, setConfirmPassword] = useState<string>()
  const [firstName, setFirstName] = useState<string>()
  const [lastName, setLastName] = useState<string>()

  // Flag: indicates if the user already has an account.
  const [hasAccount, setHasAccount] = useState<boolean>(false)

  const [isNewLogin, setIsNewLogin] = useState<boolean | undefined>()
  const [clearForm, setClearForm] = useState<boolean | undefined>(false)

  const eventId = resolveRegistrationEventId(router.query.eventId, eventIdsMap)

  const [ticketPersist, setTicketPersist] = useLocalStorage<{
    ticketName?: string
    discountCode?: string
    additionalTickets?: number
    eventId?: string
  }>('Forumm_Registration_Data', {})

  const [redirectTarget, setRedirectTarget] = useLocalStorage<
    RedirectTarget | undefined
  >('Forumm_redirect_target', undefined)

  const setEventRedirectTarget = () => {
    setRedirectTarget({
      path: router.asPath,
      analyticsIntent: 'buy_ticket',
      event: {
        eventId: eventId as string,
        title: event?.event?.title ?? 'Event',
      },
    })
  }

  const handleGoogleSignIn = async () => {
    setEventRedirectTarget()
    await signInWithGoogle()
  }

  const handleLinkedInSignIn = async () => {
    setEventRedirectTarget()
    await signInWithLinkedIn()
  }

  const [ticketData, setTicketData] = useState<{
    requiresAdditional: boolean
    ticketName?: string
    additionalTickets: TicketPurchaseInput[]
    totalPrice?: number
    discountValid?: boolean
    discountCode?: string
    discountTitle?: string
    discountType?: DiscountType
    discountPercentage?: number
  }>({
    requiresAdditional: false,
    // additionalTickets: ticketPersist.additionalTickets
    //   ? new Array().fill('', 0, ticketPersist?.additionalTickets)
    //   : [],
    // discountCode: ticketPersist?.discountCode,
    additionalTickets: [],
  })
  const [fieldVals, setFieldVals] = useState<any[]>([])
  const [errors, setErrors] = useState<
    {
      step: string
      message: string | ReactNode
      eleId?: string
      alert?: boolean
    }[]
  >([])
  const [attendeesError, setAttendeesError] = useState<string>('')
  const [saving, setSaving] = useState<boolean>(false)
  const [signedIn, setSignedIn] = useState<boolean>(false)
  const authenticatedEmailRef = useRef<string | undefined>()
  const authenticatedFullNameRef = useRef<string | undefined>()
  const loginEventTrackedRef = useRef(false)

  const signInForRegistration = async (email: string, password: string) => {
    const session = await signInWithEmail(email, password)

    if (!loginEventTrackedRef.current) {
      pushFunnelLoginEvent('buy_ticket')
      loginEventTrackedRef.current = true
    }

    return session
  }
  const ticketFirstAccountGateRef = useRef(false)
  const [tab, setTab] = useState('Register')
  const [alreadyJoined, setAlreadyJoined] = useState<boolean>(false)
  const [event, setEvent] = useState<Partial<Event> | undefined>(undefined)
  const activeEventIdRef = useRef(eventId)
  const [accountError, setAccountError] = useState(false)
  const [regFieldSelectedAttendee, setRegFieldSelectedAttendee] =
    useState<MiniNavInput>({ stage: 0 })

  const availableTickets = getAvailableTickets(event!)
  const selectedTicketName = (
    ticketData?.ticketName ??
    (availableTickets.length === 1
      ? availableTickets[0]?.ticketTitle
      : undefined)
  )?.toUpperCase()
  const selectedTicketNameForMultiple = selectedTicketName
  const foundTicket = event?.tickets?.find(
    (ticket) => ticket.title.toUpperCase() === selectedTicketName
  )
  const foundTicketForMultiple = event?.tickets?.find(
    (ticket) => ticket.title.toUpperCase() === selectedTicketNameForMultiple
  )
  const foundAvailableTicketForMultiple = availableTickets.find(
    (ticket) =>
      ticket.ticketTitle?.toUpperCase() === selectedTicketNameForMultiple
  )
  const selectedTicketAllowsMultiple = !!(
    foundTicketForMultiple?.allowMultiplePurchase ??
    foundAvailableTicketForMultiple?.allowMultiplePurchase
  )
  const activeAdditionalTickets =
    ticketData.requiresAdditional && selectedTicketAllowsMultiple
      ? ticketData.additionalTickets
      : []

  const setMissingTicketError = () => {
    setErrors((state) => [
      ...state.filter((error) => error.eleId !== 'ticketType'),
      {
        eleId: 'ticketType',
        message: 'Please select a ticket.',
        step: 'Register',
      },
    ])
  }

  const [getEventForUnregisteredUser] = useLazyQuery(
    GET_EVENT_BY_ID_FOR_UNREGISTERED_USER
  )

  const [getUserStatus] = useLazyQuery(GET_USER_STATUS)

  const [checkDiscountCode] = useLazyQuery(CHECK_DISCOUNT_CODE)

  const membershipAccess = getPermission('USER::MEMBERSHIP::*')

  const hasMembership = membershipAccess.includes(
    event?.event?.organizationName ?? '*'
  )

  const { data: fundraiseData } = useQuery(GET_EVENT_BY_DONATION_URL, {
    variables: {
      input: {
        donationUrl: event?.event?.donationUrl as string,
      },
    },
    skip: !event?.event?.donationUrl,
  })

  const fundraiseId = fundraiseData?.getEventByDonationUrl?.eventId
  const fundraise = fundraiseData?.getEventByDonationUrl?.event as EventDetails
  const fundraiseDetails = fundraiseData?.getEventByDonationUrl
    ?.fundraising as EventFundraising

  const programmes = fundraise
    ? [
        {
          description: fundraise.description,
          goal: fundraiseDetails.goal,
          media: fundraiseDetails?.media?.[0],
          title: fundraiseDetails.title ?? `Main Fund`,
        } as FundraisingProgram,
        ...(fundraiseDetails?.programs ?? []),
      ]
    : []

  const [donationData, setDonationData] = useState<RegistrationDonation>({
    enabled: false,
  })

  const setAuthenticatedEmail = (value?: string | null) => {
    authenticatedEmailRef.current = normalizeEmail(value)
  }

  const getSessionAccountData = (
    session?: CognitoSessionLike,
    fallbackEmail?: string | null
  ): RegistrationAccountData => {
    const payload = session?.getIdToken?.()?.payload
    const sessionEmail = payload?.email ?? fallbackEmail
    const fullName =
      payload?.['custom:fullName']?.trim() ||
      payload?.name?.trim() ||
      [payload?.given_name, payload?.family_name]
        .filter((name) => !!name?.trim())
        .join(' ')
        .trim()
    const [sessionFirstName, ...sessionLastName] = (fullName ?? '')
      .split(' ')
      .map((name) => name.trim())
      .filter(Boolean)

    return {
      email: sessionEmail ?? undefined,
      firstName: sessionFirstName,
      lastName: sessionLastName.join(' '),
    }
  }

  const setAuthenticatedSession = (
    session?: CognitoSessionLike,
    fallbackEmail?: string | null
  ) => {
    const accountData = getSessionAccountData(session, fallbackEmail)
    const sessionEmail = accountData.email
    setAuthenticatedEmail(sessionEmail)
    if (sessionEmail) setEmail(sessionEmail)
    setHasAccount(true)
    setSignedIn(true)

    const fullName = [accountData.firstName, accountData.lastName]
      .filter((name) => !!name?.trim())
      .join(' ')
      .trim()
    if (!fullName) return

    authenticatedFullNameRef.current = fullName
    setFirstName(accountData.firstName)
    setLastName(accountData.lastName)
  }

  const syncAuthenticatedSession = async () => {
    try {
      const session = await getSession()
      setAuthenticatedSession(session)
      return getSessionAccountData(session)
    } catch (error) {
      console.error('Unable to sync authenticated registration session:', error)
      return false
    }
  }

  const getPrimaryFullName = (
    attendee?: EventAttendee,
    accountData?: RegistrationAccountData
  ) => {
    const formFullName = [
      accountData?.firstName ?? firstName,
      accountData?.lastName ?? lastName,
    ]
      .filter((name) => !!name?.trim())
      .join(' ')
      .trim()

    return (
      profile?.fullName?.trim() ||
      authenticatedFullNameRef.current?.trim() ||
      attendee?.name?.trim() ||
      formFullName ||
      undefined
    )
  }

  const isAuthenticatedForCurrentEmail = (emailOverride?: string | null) => {
    const currentEmail = normalizeEmail(emailOverride ?? email)
    const profileEmail = normalizeEmail(profile?.email)

    return (
      signedIn ||
      (!!currentEmail && authenticatedEmailRef.current === currentEmail) ||
      (!!currentEmail && !!profileEmail && profileEmail === currentEmail)
    )
  }

  const { loading, data } = useQuery(GET_EVENT_BY_ID, {
    variables: {
      input: {
        eventId: eventId as string,
      },
    },
    skip: !router.query.eventId || (!!userData && !profile),
  })

  useEffect(() => {
    activeEventIdRef.current = eventId
    setEvent((currentEvent) =>
      currentEvent?.eventId === eventId ? currentEvent : undefined
    )
  }, [eventId])

  useEffect(() => {
    if (ticketData.discountCode && ticketData.discountCode?.length > 0)
      checkDiscount(ticketData.discountCode)
    else
      setTicketData((state) => ({
        ...state,
        discountValid: undefined,
      }))

    // console.log({ ticketData })
  }, [
    ticketData.discountCode,
    ticketData.ticketName,
    activeAdditionalTickets.length,
    ticketData.requiresAdditional,
  ])

  useEffect(() => {
    if (errors.filter((f) => f.eleId === 'discountCode').length > 0) {
      if (!ticketData.discountCode || ticketData.discountCode === '')
        setErrors((state) => [
          ...state.filter((f) => f.eleId !== 'discountCode'),
        ])
    }
  }, [errors, ticketData.discountCode])

  useEffect(() => {
    if (profile) setSignedIn(true)
    if ((tab === 'prompt' || tab === 'Account') && (signedIn || !!profile))
      setTab('Register')
  }, [profile, hasAccount, isNewLogin, signedIn, tab])

  useEffect(() => {
    setClearForm(hasAccount)
    if (!hasAccount) {
      authenticatedEmailRef.current = undefined
      authenticatedFullNameRef.current = undefined
      if (!profile) setSignedIn(false)
    }
  }, [hasAccount, profile])

  useEffect(() => {
    console.log(`[Registration]: Current Tab<${tab}>.`)
  }, [tab])

  useEffect(() => {
    if (!hasAccount) checkEmail(email)
    else
      setErrors((state) => [...state.filter((e) => e.eleId !== 'PrimaryEmail')])
  }, [hasAccount, isNewLogin])

  useEffect(() => {
    const queriedEvent = data?.getEventById as Event | undefined
    if (queriedEvent) {
      if (queriedEvent.eventId === activeEventIdRef.current)
        setEvent(queriedEvent)
      return
    }

    if (!data && eventId && userData && !profile) {
      const requestedEventId = eventId
      getEventForUnregisteredUser({
        variables: {
          input: {
            eventId: eventId as string,
            userEmail: encodeURIComponent(userData as string),
          },
        },
      }).then((d) => {
        const queriedEvent = d.data?.getEventByIdForUnregisteredUser as
          | Event
          | undefined
        if (
          queriedEvent?.eventId === requestedEventId &&
          requestedEventId === activeEventIdRef.current
        )
          setEvent(queriedEvent)

        if (d.error) router.push(`/`)
      })
    }
  }, [data, eventId, userData])

  useEffect(() => {
    if (!event) return
    if (
      tab === 'prompt' &&
      !getRegistrationConfig(CONFIG_OPTIONS.SHOW_ACCOUNT_PROMPT, event)
    )
      setTab(signedIn || profile ? 'Register' : 'Account')
  }, [event])

  const currency = currencies[event?.event?.currency?.toUpperCase() ?? 'GBP']
  const { refreshTheme, theme } = useTheme()

  // dynamic stripe account loadin
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null)
  const [merchantNotConfigured, setMerchantNotConfigured] =
    useState<boolean>(false)
  const [loadingMerchantInfo, setLoadingMerchantInfo] = useState<boolean>(true)

  const { profile: organisationProfile } = useOrganisationProfile(
    event?.event?.organizationName || ''
  )

  const stripeReady = !merchantNotConfigured && !loadingMerchantInfo

  // dynamic stripe account loadin
  useEffect(() => {
    if (organisationProfile?.name) {
      setLoadingMerchantInfo(false)
      if (organisationProfile?.merchant) {
        const stripeAccount = organisationProfile.merchant as string
        setStripePromise(
          loadStripe(stripePublicKey, {
            stripeAccount,
          })
        )
      } else {
        setMerchantNotConfigured(true)
      }
    }
  }, [organisationProfile])

  const isInvoiceTicket = foundTicket?.ticketType === TicketType.InvoiceTicket
  const invoiceTicketTotal =
    (foundTicket?.price ?? 0) * ((activeAdditionalTickets.length ?? 0) + 1)

  useEffect(() => {
    const quantity =
      (event?.isPremium && hasMembership ? 0 : 1) +
      (activeAdditionalTickets.length ?? 0)
    const price = isInvoiceTicket ? 0 : foundTicket?.price!

    let discount = 0
    let totalPrice = price * quantity

    if (ticketData.discountType === DiscountType.Percentage) {
      discount = price * ((ticketData.discountPercentage ?? 0) / 100)
      totalPrice = (price - discount) * quantity
    }

    if (ticketData.discountType === DiscountType.BuyOneGetOneFree) {
      discount = Math.floor(quantity / 2) * price
      totalPrice = price * quantity - discount
    }

    const donationAmount = 0
    // console.log({
    //   quantity,
    //   discount,
    //   price,
    //   donationAmount,
    //   type: ticketData.discountType,
    //   cond1: ticketData.discountType === DiscountType.Percentage,
    //   cond2: ticketData.discountType === DiscountType.BuyOneGetOneFree,
    // })
    setTicketData((prevTicketData) => ({
      ...prevTicketData,
      totalPrice: donationAmount + totalPrice,
    }))
    if (ticketData.ticketName)
      setTicketPersist({
        ticketName: ticketData.ticketName,
        discountCode: ticketData.discountCode,
        additionalTickets: activeAdditionalTickets.length,
        eventId,
      })
  }, [
    activeAdditionalTickets.length,
    ticketData.requiresAdditional,
    ticketData.ticketName,
    donationData.amount,
    ticketData.discountCode,
    ticketData.discountValid,
    ticketData.discountPercentage,
    isInvoiceTicket,
  ])

  useEffect(() => {
    if (
      availableTickets.length === 1 &&
      ticketData.ticketName !== availableTickets[0].ticketTitle?.toUpperCase()
    ) {
      setTicketData((prevTicketData) => ({
        ...prevTicketData,
        ticketName: availableTickets[0].ticketTitle!.toUpperCase(),
      }))
    }
  }, [availableTickets, ticketData.ticketName, availableTickets?.length])

  useEffect(() => {
    if (!!ticketData?.ticketName && !loading && !!event) {
      const targetTicket = availableTickets?.find(
        (f) =>
          f?.ticketTitle?.toUpperCase() ===
          (ticketData?.ticketName as string)?.toUpperCase()
      )
      const remaining = parseInt(targetTicket?.remaining ?? '0')

      const orderQuantity = (activeAdditionalTickets?.length ?? 0) + 1

      const hasEnoughRemainingTickets = remaining >= orderQuantity

      if (hasEnoughRemainingTickets) {
        setErrors((state) => [
          ...state.filter((f) => f.eleId !== 'TicketQuantity'),
        ])
      } else {
        setErrors((state) => [
          ...state.filter((f) => f.eleId !== 'TicketQuantity'),
          {
            step: 'Register',
            eleId: 'TicketQuantity',
            message: 'Quantity of selected ticket too low for order.',
          },
        ])
        // setTicketData({
        //   ...ticketData,
        //   ticketName: undefined,
        // })
      }
    }
  }, [
    ticketData.ticketName,
    activeAdditionalTickets?.length,
    ticketData.requiresAdditional,
    loading,
    event,
  ])

  useEffect(() => {
    if (
      event?.availableTickets?.tickets &&
      event?.availableTickets?.tickets.length === 1 &&
      (!ticketData?.ticketName || ticketData?.ticketName?.length < 1)
    ) {
      setTicketData({
        ...ticketData,
        ticketName: (
          event?.availableTickets?.tickets[0]?.ticketTitle ?? ''
        ).toUpperCase(),
      })
    }
  }, [event?.availableTickets?.tickets])

  useEffect(() => {
    if (!!errors && errors.filter((f) => f.step === 'Account').length > 0) {
      return setTab('Account')
    }

    if (!!errors && errors.filter((f) => f.step === 'Register').length > 0) {
      return setTab('Register')
    }
  }, [errors])

  useEffect(() => {
    if (!event) return

    if (!ticketPersist?.eventId || ticketPersist.eventId !== eventId)
      setTicketPersist({})

    if (!discountedUrl && ticketPersist.eventId === eventId) {
      const foundTicket = event?.tickets?.find(
        (t) => t.title.toUpperCase() === ticketPersist.ticketName?.toUpperCase()
      )

      if (foundTicket) {
        setTicketData((state) => ({
          ...state,
          ticketName: foundTicket.title,
        }))

        // if (ticketPersist.discountCode)
        //   checkDiscount(ticketPersist.discountCode)
      }
    }

    if (discountedUrl) {
      try {
        const decodedDiscountData = JSON.parse(discountedUrl)
        const foundTicket = event?.tickets?.find((t) => {
          return (
            t.title.toUpperCase() === decodedDiscountData?.name?.toUpperCase()
          )
        })

        if (foundTicket) {
          setTicketPersist({})
          setTicketData((state) => ({
            ...state,
            ticketName: foundTicket.title,
            discountCode: decodedDiscountData.code as string,
            discountValid: undefined,
            discountType: undefined,
            discountTitle: undefined,
            discountPercentage: undefined,
          }))
        }
      } catch (err) {
        console.error(err)
      }
    }
  }, [discountedUrl, event?.tickets])

  const getEmail = (
    isAttendee?: EventAttendee | undefined,
    emailOverride?: string | null
  ) => {
    if (profile && profile.email) return profile.email
    if (isAttendee && isAttendee.email) return isAttendee.email
    if (emailOverride !== '' && emailOverride !== undefined)
      return emailOverride
    if (email !== '' && email !== undefined) return email
    return null
  }

  const submitTabEventDetails = async (
    donation?: RegistrationDonation,
    accountData?: RegistrationAccountData
  ) => {
    // If user has been added as an attendee, bypass stripe.
    if (saving) return
    const loadedEventId = event?.eventId
    if (!eventId || !loadedEventId || loadedEventId !== eventId) {
      setErrors([
        {
          message:
            'The event changed while registration was in progress. Please refresh and try again.',
          step: 'request',
        },
      ])
      setSaving(false)
      return
    }
    setErrors([])
    setSaving(true)
    const submissionEmail = accountData?.email ?? email
    const submissionPassword = accountData?.password ?? password

    const invalidPassword = validate(submissionPassword, {
      pattern: {
        value: PASSWORD_PATTERN,
        type: 'match',
        message:
          'Password doesn&amp;t meet minimum requirements. 2 lowercase, 2 uppercase, 2 numbers, and 1 special character.',
      },
    })
    if (invalidPassword) {
      setTab('Account')
      return setErrors((state) => [
        ...state.filter((f) => f.step !== 'Account'),
        { message: invalidPassword, step: 'Account' },
      ])
    }
    let redirectionUrl: any

    const isAttendee = event?.attendees?.find((s) => {
      if (!submissionEmail) return false
      const res = s.email?.toLowerCase() === submissionEmail?.toLowerCase()
      return res
    })

    const isSpeaker = event?.speakers?.find((s) => {
      if (!submissionEmail) return false
      const res = s.email?.toLowerCase() === submissionEmail?.toLowerCase()
      return res
    })

    const missingTicketSelection =
      !isAttendee &&
      !isSpeaker &&
      event?.event?.eventType !== EventType.Fundraiser &&
      !selectedTicketName

    const primaryFullName = getPrimaryFullName(isAttendee, accountData)
    if (!primaryFullName) {
      setErrors([
        {
          message:
            'We could not load your profile details. Please refresh and try again.',
          step: 'Register',
        },
      ])
      setTab('Register')
      setSaving(false)
      return
    }

    if (missingTicketSelection) {
      setMissingTicketError()
      setTab('Register')
      setSaving(false)
      return
    }

    const userMetadata = (
      fieldVals && Array.isArray(fieldVals) ? fieldVals : [...fieldVals]
    )?.map((fv) =>
      Object.keys(fv).map((key) => ({
        name: key,
        value: fv[key],
      }))
    )

    if (isNewLogin && !signedIn) {
      const session = await signInForRegistration(
        submissionEmail!,
        submissionPassword!
      )
      setAuthenticatedSession(session, submissionEmail)
      setSaving(false)
      window.location.href = `/event/${router.query.eventId}/register`
      return
    }

    const parsedTickets = () => {
      const mainTicket: TicketPurchaseInput = {
        title:
          isAttendee && isAttendee.ticketTitle
            ? isAttendee.ticketTitle
            : isSpeaker
              ? 'SPEAKER'
              : event?.event?.eventType === EventType.Fundraiser
                ? 'FUNDRAISER_TICKET'
                : selectedTicketName!,
        email: getEmail(isAttendee, submissionEmail),
        fullName: primaryFullName,
        registrationVisible: isRegistrationVisible,
        discountCode: ticketData?.discountValid
          ? ticketData?.discountCode
          : undefined,
      }

      const getDiscount = (orderIndex: number) => {
        const payload = ticketData?.discountValid
          ? ticketData?.discountCode
          : undefined
        const nTickets = activeAdditionalTickets.length + 1
        if (ticketData.discountType === DiscountType.BuyOneGetOneFree) {
          const isPairedTicket = orderIndex < nTickets || orderIndex % 2 === 0
          if (!isPairedTicket) return undefined
        }

        return payload
      }

      const additionalTickets: TicketPurchaseInput[] =
        activeAdditionalTickets.map((t, i) => ({
          title: t.title,
          email: t.email,
          fullName: t.fullName,
          registrationFields: userMetadata?.[i + 1],
          registrationVisible: isRegistrationVisible,
          discountCode: getDiscount(i + 1),
        }))

      return [mainTicket, ...additionalTickets]
    }

    const additionalFields = userMetadata?.[0] || []

    const isAuthenticatedForSubmission =
      isAuthenticatedForCurrentEmail(submissionEmail)

    redirectionUrl = await purchaseTickets({
      variables: {
        input: {
          hasBalance: !isAttendee,
          accountToCreate: isAuthenticatedForSubmission
            ? undefined
            : {
                email: isAttendee ? isAttendee.email : submissionEmail!,
                fullName: primaryFullName,
                password: submissionPassword,
              },
          eventId: loadedEventId,
          tickets: parsedTickets(),
          userData: additionalFields,
          embedCheckoutReturn: options.embedCheckoutReturn ? true : undefined,
          embeddedCheckout: options.embedCheckoutReturn ? true : undefined,
        },
        ...(!!donation && donationData?.enabled
          ? {
              donation: {
                amount: donation.amount! * 100,
                firstName: (accountData?.firstName ?? firstName)!,
                email: getEmail(isAttendee, submissionEmail) as string,
                organizationName: event?.event?.organizationName!,
                eventId: fundraiseId as string,
                giftAid: !!donation.giftAid,
                eventName: event?.event?.title!,
                currency: donation.currency,
                lastName: (accountData?.lastName ?? lastName)!,
                message: donation.message ?? '',
                selectedProgram: donation.selectedProgram ?? '',
                statisticId: statisticId,
                paymentType: donation.paymentType,
                coverFee: donation.coverFee ? 'true' : 'false',
                address: donation.address!,
                homeAddress: donation.homeAddress,
                visibility: donation.visibility,
                donorTitle: donation.donorTitle,
                donorDob: donation.donorDob,
                allowEmailContact: donation.allowEmailContact,
                allowTelephoneContact: donation.allowTelephoneContact,
                addressLine1: donation.addressLine1,
                addressLine2: donation.addressLine2,
                addressLine3: donation.addressLine3,
                addressPostalCode: donation.addressPostalCode,
                addressCity: donation.addressCity,
                addressCountry: donation.addressCountry,
                addressPhone: donation.addressPhone,
                directDebitName: donation.directDebitName,
                directDebitNumber: donation.directDebitNumber,
                directDebitSort: donation.directDebitSort,
                directDebitConfirmed: donation.directDebitConfirmed,
                directDebitPeriod: donation.directDebitPeriod,
                donation: donation.donation,
                fee: donation.fee,
                avatarUrl: donation.avatarUrl,
              },
            }
          : {}),
      },
    })

    setAccountError(false)
    const checkout = redirectionUrl?.data?.purchaseTickets
    const checkoutUrl = checkout?.checkoutUrl
    const checkoutClientSecret = checkout?.checkoutClientSecret
    const checkoutSessionId = checkout?.checkoutSessionId
    const checkoutStripeAccountId = checkout?.checkoutStripeAccountId
    const userExists = checkout?.userAlreadyExist

    if (checkoutUrl && checkoutUrl.includes('payment_error')) {
      if (!checkoutUrl.includes('error=')) {
        setErrors([
          {
            message:
              'An unexpected error has occured, please contact the site admin.',
            step: 'request',
          },
        ])
      }
      const errorCode = checkoutUrl.split('error=')[1]
      if (errorCode === 'UserExists') {
        setAccountError(true)

        setErrors([
          {
            message:
              'Looks like you already have an account, please login before booking.',
            step: 'request',
          },
        ])
      }

      if (errorCode === 'InvalidSetup') {
        setErrors([
          {
            message: 'There was an issue with the event organiser.',
            step: 'request',
          },
        ])
      }
      setSaving(false)
      return
    }

    if (userExists) {
      setAccountError(true)
      setErrors([
        {
          message:
            'Looks like you already have an account, please login before booking.',
          step: 'request',
        },
      ])
      setSaving(false)
      return
    }

    if (
      !isAuthenticatedForSubmission &&
      !!redirectionUrl?.data?.purchaseTickets?.user
    ) {
      const session = await signInForRegistration(
        submissionEmail!,
        submissionPassword ??
          JSON.parse(redirectionUrl.data.purchaseTickets.user).pass
      )
      setAuthenticatedSession(session, submissionEmail)
    }

    if (checkoutClientSecret && options.embedCheckoutReturn) {
      window.parent?.postMessage(
        {
          checkoutClientSecret,
          checkoutSessionId,
          checkoutStripeAccountId,
          eventId,
          stripePublishableKey: stripePublicKey ?? '',
          type: 'FORUMM_EMBED_CHECKOUT_START',
        },
        '*'
      )
      setSaving(false)
      return
    }

    if (checkoutUrl) {
      openInSameTab(checkoutUrl)
    }

    setSaving(false)
  }

  useEffect(() => {
    if (event) {
      refreshTheme(event as Event)
    }
    if (userData) setEmail(userData)
  }, [event, refreshTheme, userData])

  useEffect(() => {
    if (profile?.userId) {
      setHasAccount(true)
    }
    if (!!profile?.email && !email) setEmail(profile.email)
  }, [profile])

  useEffect(() => {
    if (profile && event?.attendees) {
      let hasJoined =
        event?.attendees.filter((at) => {
          return (
            at.registered &&
            at.email.toLowerCase() === profile?.email?.toLowerCase()
          )
        }).length > 0

      if (!hasJoined && event?.speakers && event.speakers.length > 0)
        hasJoined =
          event?.speakers?.filter(
            (f) => f.email?.toLowerCase() === profile?.email?.toLowerCase()
          ).length > 0

      setAlreadyJoined(hasJoined)
    }
  }, [profile, event])

  const [usersData, setUsersData] = useState<any[]>([])
  const [error, setError] = useState<Record<string, string | undefined>>({})
  useEffect(() => {
    getAttendeesToGetRegFields()
  }, [usersData])

  const [attendeesToGetRegFields, setAttendeesToGetRegFields] = useState<
    TicketPurchaseInput[]
  >([])

  const [isRegistrationVisible, setIsRegistrationVisible] =
    useState<boolean>(false)

  const getAttendeesToGetRegFields = (): TicketPurchaseInput[] => {
    const payload = [
      {
        email: getEmail(),
        title: ticketData.ticketName!,
        fullName: getPrimaryFullName() ?? '',
      },
      ...activeAdditionalTickets,
    ]
    const user = payload[0]

    const id = `${payload[0].fullName}::${payload[0].email}`
      .trim()
      .toLowerCase()
      .replaceAll(' ', '_')

    const isValid = (() => {
      const currentStage = regFieldSelectedAttendee?.stage.toString()
      const allowedStages = payload.map((p) =>
        `${p.fullName}::${p.email}`.trim().toLowerCase().replaceAll(' ', '_')
      )

      if (!currentStage) return false
      return allowedStages.includes(currentStage)
    })()

    // console.log({ payload, user, regFieldSelectedAttendee, isValid })
    if (
      (!!user.fullName &&
        !!user.email &&
        (!regFieldSelectedAttendee ||
          regFieldSelectedAttendee.stage.toString().includes('undefined'))) ||
      !isValid
    )
      setAttendeesToGetRegFields(payload)
    return payload
  }

  const errorHandler =
    (id: string, alert: boolean = false) =>
    (message: string, testId: string) => {
      const payload = [...errors]

      const hasError = !!message || message !== ''
      const isCleared = payload.find((f) => f.eleId === testId) && !hasError

      if (isCleared) return setErrors(payload.filter((f) => f.eleId !== testId))

      const isDuplicate = !!payload.find((p) => p.eleId === testId)

      const newError = {
        step: id ?? 'unknown',
        message,
        eleId: testId,
        alert,
      }

      if (!isDuplicate && hasError) setErrors([...payload, newError])
    }

  const totalTickets = activeAdditionalTickets.length + 1
  const fieldsToValidate = (event?.registrationFields ?? [])
    .filter((field) => field.required === true)
    .map((field) => field?.name)

  const allTicketsRegistrationFieldsAreFilled = useMemo(() => {
    return areAllTicketRegistrationFieldsFilled(
      fieldVals,
      totalTickets,
      fieldsToValidate
    )
  }, [fieldVals, totalTickets, fieldsToValidate])

  const disableFormProgress = useMemo(() => {
    if (tab === 'Attendee Information') {
      if (errors.find((f) => f.step === 'Attendee Information')) return true

      return activeAdditionalTickets.some((att) => !att.fullName || !att.email)
    }

    return false
  }, [tab, ticketData, activeAdditionalTickets])

  const dynamicStyle = getDynamicStyle(theme.highlightColour)
  const isDarkTheme = theme.type === 'DARK'
  const eventImage =
    event?.event?.bannerImage ??
    'https://assets.tumblr.com/images/default_header/optica_pattern_11.png'

  // console.log({ firstName, lastName })

  const organisationTrxFee =
    organisationProfile?.percentage !== undefined
      ? organisationProfile.percentage
      : 10

  const handleAdditionalTickets = (val: number) => {
    setTicketData((state) => ({
      ...state,
      additionalTickets: getAdditionalTicketsForCount({
        currentTickets:
          state.requiresAdditional && selectedTicketAllowsMultiple
            ? state.additionalTickets
            : [],
        requestedCount: val,
        enabled: state.requiresAdditional && selectedTicketAllowsMultiple,
        selectedTicketName: state.ticketName,
        defaultTicketTitle: availableTickets[0]?.ticketTitle ?? undefined,
      }),
    }))
  }

  const setRequiresAdditionalTickets = (requiresAdditional: boolean) => {
    const canRequireAdditional =
      requiresAdditional && selectedTicketAllowsMultiple

    if (!canRequireAdditional) {
      setAttendeesError('')
      setAttendeesToGetRegFields([])
      setRegFieldSelectedAttendee({ stage: 0 })
      setFieldVals((state) => state.slice(0, 1))
      setError((state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => key.startsWith('0::'))
        )
      )
      setErrors((state) =>
        state.filter((error) => error.step !== 'Attendee Information')
      )
    }

    setTicketData((state) => {
      const defaultTicketInfo = availableTickets[0]
      const additionalTickets = !canRequireAdditional
        ? []
        : state.additionalTickets.length < 1 && defaultTicketInfo
          ? [
              {
                fullName: '',
                title: state.ticketName ?? defaultTicketInfo.ticketTitle!,
              },
            ]
          : state.additionalTickets

      return {
        ...state,
        requiresAdditional: canRequireAdditional,
        additionalTickets,
      }
    })
  }

  const continueAfterTicketSelection = async (
    goToTab: (tab: string) => void,
    accountData?: RegistrationAccountData
  ) => {
    const nextEmail = accountData?.email ?? email
    const nextPassword = accountData?.password ?? password
    const nextConfirmPassword = accountData?.confirmPassword ?? confirmPassword

    if (ticketData.requiresAdditional && activeAdditionalTickets.length < 1) {
      setRequiresAdditionalTickets(true)
    }

    getAttendeesToGetRegFields()

    if (!hasAccount && !!nextPassword && nextPassword !== nextConfirmPassword) {
      setErrors([
        {
          step: 'confirm_password',
          message: 'Please ensure that your given passwords match.',
        },
      ])
      return
    }

    const captureRegistrationFields =
      (event?.attendees?.find((attendee) => attendee.email === nextEmail)
        ?.requireRegistrationFields ??
        true) ||
      activeAdditionalTickets.length > 0

    const hasRegistrationFields =
      !!event?.registrationFields && event.registrationFields.length > 0

    if (ticketData.requiresAdditional) return goToTab('Attendee Information')

    if (
      ((event?.registrationFields?.length ?? 0) > 0 || fieldVals) &&
      captureRegistrationFields &&
      hasRegistrationFields
    )
      return goToTab('Event Details')

    return submitTabEventDetails(undefined, accountData)
  }

  const continueFromAccountStep = async ({
    accountData,
    goToTab = setTab,
    onSignedIn,
    validatePasswordConfirmation = !hasAccount,
  }: RegistrationStepOptions = {}) => {
    const submittedEmail = accountData?.email ?? email
    const submittedPassword = accountData?.password ?? password
    const submittedConfirmPassword =
      accountData?.confirmPassword ?? confirmPassword

    if (accountData) {
      setFirstName(accountData.firstName ?? firstName)
      setLastName(accountData.lastName ?? lastName)
      setEmail(submittedEmail)
      setPassword(submittedPassword)
      setConfirmPassword(submittedConfirmPassword)
    }

    setErrors([])
    const shouldResumeTicketFlow = ticketFirstAccountGateRef.current

    if (hasAccount && !!submittedEmail && !!submittedPassword) {
      try {
        const session = await signInForRegistration(
          submittedEmail,
          submittedPassword
        )
        setAuthenticatedSession(session, submittedEmail)
        setSaving(false)
        if (shouldResumeTicketFlow) {
          ticketFirstAccountGateRef.current = false
          return continueAfterTicketSelection(goToTab, {
            ...accountData,
            email: submittedEmail,
            password: submittedPassword,
          })
        }
        if (onSignedIn) onSignedIn()
        else goToTab('Register')
        return
      } catch (_ex) {
        setSaving(false)
        setErrors([
          {
            step: 'login',
            message: 'Incorrect email or password, please try again.',
          },
        ])
        return
      }
    }

    if (validatePasswordConfirmation) {
      const isValidPassword = submittedPassword === submittedConfirmPassword
      if (!!submittedPassword && !isValidPassword) {
        setErrors([
          {
            step: 'confirm_password',
            message: 'Please ensure that your given passwords match.',
          },
        ])
        return
      }
    }

    if (shouldResumeTicketFlow) {
      ticketFirstAccountGateRef.current = false
      return continueAfterTicketSelection(goToTab, {
        ...accountData,
        confirmPassword: submittedConfirmPassword,
        email: submittedEmail,
        password: submittedPassword,
      })
    }

    goToTab('Register')
  }

  const continueFromTicketStep = async ({
    goToTab = setTab,
    onSignedIn,
  }: RegistrationStepOptions = {}) => {
    if (
      !isAuthenticatedForCurrentEmail() &&
      hasAccount &&
      !!email &&
      !!password
    ) {
      try {
        const session = await signInForRegistration(email, password)
        setAuthenticatedSession(session, email)
        setSaving(false)
        if (onSignedIn) {
          onSignedIn()
          return
        }
      } catch (_ex) {
        setSaving(false)
        setErrors([
          {
            step: 'login',
            message: 'Incorrect email or password, please try again.',
          },
        ])
        return
      }
    }

    const hasCurrentAttendeeTicket = event?.attendees?.some(
      (attendee) =>
        !!email && attendee.email?.toLowerCase() === email.toLowerCase()
    )
    const hasCurrentSpeakerTicket = event?.speakers?.some(
      (speaker) =>
        !!email && speaker.email?.toLowerCase() === email.toLowerCase()
    )
    const missingTicketSelection =
      !hasCurrentAttendeeTicket &&
      !hasCurrentSpeakerTicket &&
      event?.event?.eventType !== EventType.Fundraiser &&
      !selectedTicketName

    if (missingTicketSelection) {
      setMissingTicketError()
      goToTab('Register')
      return
    }

    const hasAccountStepInput = !!(
      firstName ||
      lastName ||
      password ||
      confirmPassword
    )

    if (!isAuthenticatedForCurrentEmail() && !hasAccountStepInput) {
      ticketFirstAccountGateRef.current = true
      goToTab('Account')
      return
    }

    ticketFirstAccountGateRef.current = false
    return continueAfterTicketSelection(goToTab)
  }

  const videoUrl = getRegistrationVideoUrl(event?.eventId)

  const validateRegistrationFields = (
    event: Partial<Event>,
    usersData: any[],
    fieldVals: any[],
    selectedAttendeeIndex?: number
  ) => {
    const payload = getRegistrationFieldErrors({
      event,
      usersData,
      fieldVals,
      selectedAttendeeIndex,
    })
    const activeErrors = Object.values(payload).filter((p) => !!p)

    if (activeErrors.length > 0) {
      setError((state) => {
        return {
          ...(state ?? {}),
          ...payload,
        }
      })
      return false
    }

    return true
  }

  const handleRegistrationFieldChange = (
    attendeeIndex: number,
    fieldName: string,
    value: unknown
  ) => {
    const payload = [...fieldVals]

    payload[attendeeIndex] = {
      ...payload[attendeeIndex],
      [fieldName]: value,
    }

    const errorKey = `${attendeeIndex}::${fieldName
      .replaceAll(' ', '_')
      .toLowerCase()}`

    setError((state) => {
      if (!state?.[errorKey]) return state

      return {
        ...state,
        [errorKey]: undefined,
      }
    })
    setFieldVals(payload)
  }

  const handleRegistrationFieldPrevious = (attendeeIndex: number) => {
    if (attendeeIndex > 0) {
      setRegFieldSelectedAttendee({
        stage: attendeeIndex - 1,
      })
    }
  }

  const handleRegistrationFieldNext = (
    attendeeIndex: number,
    attendeeCount: number
  ) => {
    const validRegFields = validateRegistrationFields(
      event!,
      usersData,
      fieldVals,
      attendeeIndex
    )

    if (validRegFields && attendeeIndex < attendeeCount - 1) {
      setRegFieldSelectedAttendee({
        stage: attendeeIndex + 1,
      })
    }
  }

  const checkDiscount = useCallback(
    debounce(async (val: string) => {
      await checkDiscountCode({
        variables: {
          eventId,
          ticketName: ticketData.ticketName,
          code: val,
        },
        onCompleted: (data) => {
          setErrors((state) => [
            ...state.filter((f) => f.eleId !== 'discountCode'),
          ])

          const payload = {
            discountValid: data?.checkDiscountCode?.success ?? false,
            discountType: data?.checkDiscountCode?.type,
            discountTitle: data?.checkDiscountCode?.discountTitle,
            discountPercentage: data?.checkDiscountCode?.percentage,
          }
          setTicketData((state) => ({
            ...state,
            ...payload,
          }))

          const setDiscountError = (msg: string) =>
            setErrors((state) => [
              ...state.filter((f) => f.eleId !== 'discountCode'),
              {
                eleId: 'discountCode',
                message: msg,
                step: 'Register',
              },
            ])

          if (
            payload.discountValid !== undefined &&
            payload.discountValid === false
          )
            setDiscountError('Invalid discount code')

          const targetTicket = event?.availableTickets?.tickets?.find(
            (t) =>
              ticketData.ticketName?.toUpperCase() ===
              t.ticketTitle?.toUpperCase()
          )
          const normalizedDiscountTitle = payload.discountTitle?.toUpperCase()
          const targetDiscount = targetTicket?.discountCodes?.find(
            (f) =>
              !!normalizedDiscountTitle &&
              f.title.toUpperCase() === normalizedDiscountTitle
          )
          const nDiscounts: number = (() => {
            let nTickets = activeAdditionalTickets.length + 1
            // Handle when an odd number of tickets are purchased using a 2-4-1 code.
            if (payload.discountType === DiscountType.BuyOneGetOneFree) {
              if (nTickets % 2 !== 0) nTickets -= 1
              return nTickets / 2
            }
            return nTickets
          })()

          if (targetDiscount && (targetDiscount?.remaining ?? 0) < nDiscounts)
            setDiscountError('Discount code usage limit reached')
        },
      })
    }, 1000),
    [
      ticketData.ticketName,
      activeAdditionalTickets.length,
      ticketData.requiresAdditional,
      event?.availableTickets,
    ]
  )

  const checkEmail = useCallback(
    debounce(async (val: string) => {
      if (!!val) {
        await getUserStatus({
          variables: {
            email: val,
          },
          onCompleted: (res) => {
            const result = res?.getUserStatus?.exists
            if (result)
              setErrors((state) => [
                ...state,
                {
                  step: 'Account',
                  eleId: 'PrimaryEmail',
                  message: (
                    <Box
                      className="text-red-500 border-red-500 font-normal"
                      ignoreTheme
                    >
                      Looks like you already have a Forumm account, try logging
                      in{' '}
                      <span
                        className="font-bold cursor-pointer"
                        onClick={() => {
                          setIsNewLogin(true)
                          setHasAccount(true)
                        }}
                      >
                        here
                      </span>
                      .
                    </Box>
                  ),
                },
              ])
            else
              setErrors((state) => [
                ...state.filter((e) => e.eleId !== 'PrimaryEmail'),
              ])
          },
        })
      }
    }, 1000),
    []
  )

  const handleAdditionalTicketChange = (
    index: number,
    ticket: Partial<TicketPurchaseInput>
  ) => {
    setTicketData((state) => {
      const payload = [...state.additionalTickets]
      payload[index] = {
        ...payload[index],
        ...ticket,
      }

      return {
        ...state,
        additionalTickets: payload,
      }
    })
  }

  const handleAdditionalAttendeesContinue = () => {
    const attendeesIncompleted = activeAdditionalTickets.some(
      (attendee) => !attendee.fullName || !attendee.email
    )
    if (attendeesIncompleted) {
      setAttendeesError(
        'Please complete the information of your additional attendees.'
      )
      return
    }

    if ((event?.registrationFields?.length ?? 0) > 0 || fieldVals)
      getAttendeesToGetRegFields()

    // DOCS: Allows manually added attendees to skip the registration field capture step.
    const captureRegistrationFields =
      (event?.attendees?.find((attendee) => attendee.email === email)
        ?.requireRegistrationFields ||
        activeAdditionalTickets.length > 0) ??
      true

    const hasRegistrationFields =
      !!event?.registrationFields && event.registrationFields.length > 0

    return captureRegistrationFields && hasRegistrationFields
      ? setTab('Event Details')
      : submitTabEventDetails()
  }

  const handleRegistrationFieldsContinue = () => {
    const validRegFields = validateRegistrationFields(
      event!,
      usersData,
      fieldVals
    )

    const errors = Object.keys(error).map((key) => ({
      key: key,
      message: error[key],
    }))

    const errorStep = parseInt(
      errors.find((entry) => !!entry.message)?.key.split('::')?.[0] ?? '0'
    )

    const hasErrors = errors.map((entry) => !!entry.message).includes(true)

    const canContinue = validRegFields && !hasErrors

    if (!canContinue && errorStep >= 0)
      setRegFieldSelectedAttendee({
        stage: errorStep,
      })

    if (canContinue) {
      return submitTabEventDetails()
    }
  }

  const handleEmailChange = (email: string) => {
    setEmail(email)
    if (!hasAccount && !isNewLogin) checkEmail(email)
  }

  const vatIncluded = isVatIncludedForOrganisation(
    event?.event?.organizationName
  )

  return {
    accountError,
    activeAdditionalTickets,
    allTicketsRegistrationFieldsAreFilled,
    alreadyJoined,
    attendeesError,
    attendeesToGetRegFields,
    availableTickets,
    clearForm,
    confirmPassword,
    continueFromAccountStep,
    continueFromTicketStep,
    currency,
    disableFormProgress,
    donationData,
    dynamicStyle,
    email,
    error,
    errorHandler,
    errors,
    event,
    eventId,
    eventImage,
    fieldVals,
    firstName,
    foundTicket,
    fundraise,
    fundraiseId,
    getAttendeesToGetRegFields,
    getEmail,
    handleAdditionalAttendeesContinue,
    handleAdditionalTicketChange,
    handleAdditionalTickets,
    handleEmailChange,
    handleGoogleSignIn,
    handleLinkedInSignIn,
    handleRegistrationFieldChange,
    handleRegistrationFieldNext,
    handleRegistrationFieldPrevious,
    handleRegistrationFieldsContinue,
    hasAccount,
    invoiceTicketTotal,
    isDarkTheme,
    isInvoiceTicket,
    isNewLogin,
    isRegistrationVisible,
    lastName,
    loading,
    organisationProfile,
    organisationTrxFee,
    password,
    profile,
    programmes,
    regFieldSelectedAttendee,
    router,
    saving,
    setAttendeesError,
    setAttendeesToGetRegFields,
    setConfirmPassword,
    setDonationData,
    setError,
    setErrors,
    setFieldVals,
    setFirstName,
    setHasAccount,
    setIsNewLogin,
    setIsRegistrationVisible,
    setLastName,
    setPassword,
    setRedirectTarget,
    setRegFieldSelectedAttendee,
    selectedTicketAllowsMultiple,
    setRequiresAdditionalTickets,
    setSaving,
    setTab,
    setTicketData,
    signedIn,
    signInWithEmail,
    syncAuthenticatedSession,
    stripePromise,
    stripeReady,
    submitTabEventDetails,
    tab,
    theme,
    ticketData,
    userData,
    vatIncluded,
    videoUrl,
  }
}
